import React from 'react';
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from "react-router-dom";
import App from './App.tsx'
import "./index.css";
import "./i18n";
console.log('✅ i18n imported and initializing');
import { LanguageProvider } from "./contexts/LanguageContext";
import { UnifiedErrorScreen } from './components/error/UnifiedErrorScreen';

// Production Error Boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error; errorInfo?: React.ErrorInfo }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 Production Error Caught:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
    
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <UnifiedErrorScreen
          variant="runtime"
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleReset}
          showHomeButton={false}
        />
      );
    }
    return this.props.children;
  }
}

// Simplified service worker registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Service worker will be registered by vite-plugin-pwa
    console.log('Service Worker registration setup completed');
  });
}

// Add timeout fallback UI if app takes too long to render
const renderTimeout = setTimeout(() => {
  console.error('⚠️ App render timeout (3s) - showing fallback UI');
  const root = document.getElementById("root");
  if (root && !root.hasChildNodes()) {
    root.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #0a0a0a; color: white; font-family: system-ui, -apple-system, sans-serif;">
        <div style="text-align: center; padding: 20px;">
          <div style="width: 48px; height: 48px; border: 4px solid #333; border-top-color: #22c55e; border-radius: 50%; margin: 0 auto 20px; animation: spin 1s linear infinite;"></div>
          <h2 style="margin: 0 0 12px 0; font-size: 24px; font-weight: 600;">Loading Trade Bot 365...</h2>
          <p style="color: #888; margin: 0;">If this takes too long, please <a href="/" style="color: #22c55e; text-decoration: underline;">refresh the page</a></p>
        </div>
      </div>
      <style>
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
  }
}, 3000); // Show fallback after 3 seconds

console.log('🚀 Starting React app render...');

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <LanguageProvider>
          <App />
        </LanguageProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);

// Clear timeout after successful render
clearTimeout(renderTimeout);
console.log('✅ React app rendered successfully');
